const { CircuitGame, DIRECTIONS } = require('./circuitGame')

class CanvasCircuitApp {
  constructor(webApi, options = {}) {
    this.web = webApi
    this.adbut = options.adbut || null
    this.level = Math.max(1, Math.floor(options.level || 1))
    this.configuredSeed = Number.isFinite(options.seed) ? Math.floor(options.seed) : null
    this.game = this.createGame(this.level, this.configuredSeed)
    this.canvas = null
    this.ctx = null
    this.cell = 48
    this.boardX = 0
    this.boardY = 0
    this.boardWidth = 0
    this.boardHeight = 0
    this.headerHeight = 132
    this.footerHeight = 76
    this.lastStatus = 'ready'
    this.finishHandled = false
    this.pressedCell = null
    this.pressedAt = 0
    this.toast = null
  }

  createGame(level, seed = null) {
    const actualSeed = seed ?? Date.now() + level * 7919
    this.levelSeed = actualSeed
    return new CircuitGame({ level, seed: actualSeed })
  }

  async mount() {
    this.canvas = this.web.createCanvas()
    this.ctx = this.canvas.getContext('2d')
    if (!this.ctx) throw new Error('Canvas 2D rendering is unavailable')
    this.requestFrame = this.createRequestFrame()
    this.resize()
    this.bindInput()
    this.web.onWindowResize(() => this.resize())
    this.game.start(Date.now())
    this.lastStatus = this.game.status
    this.loop()
  }

  createRequestFrame() {
    if (typeof this.canvas?.requestAnimationFrame === 'function') {
      return this.canvas.requestAnimationFrame.bind(this.canvas)
    }
    if (typeof globalThis.requestAnimationFrame === 'function') {
      return globalThis.requestAnimationFrame.bind(globalThis)
    }
    return (callback) => setTimeout(() => callback(Date.now()), 16)
  }

  resize() {
    const info = this.web.getSystemInfoSync()
    const width = info.windowWidth
    const height = info.windowHeight
    this.canvas.width = width
    this.canvas.height = height
    this.systemInfo = info

    const padding = Math.max(12, Math.floor(width * 0.04))
    this.headerHeight = Math.max(106, Math.min(142, Math.floor(height * 0.25)))
    this.footerHeight = Math.max(62, Math.min(82, Math.floor(height * 0.14)))
    const verticalGap = Math.max(12, Math.floor(height * 0.025))
    const availableWidth = Math.max(120, width - padding * 2)
    const availableHeight = Math.max(
      120,
      height - this.headerHeight - this.footerHeight - verticalGap * 2
    )
    this.cell = Math.min(
      availableWidth / this.game.cols,
      availableHeight / this.game.rows
    )
    this.boardWidth = this.cell * this.game.cols
    this.boardHeight = this.cell * this.game.rows
    this.boardX = (width - this.boardWidth) / 2
    this.boardY =
      this.headerHeight + verticalGap + Math.max(0, (availableHeight - this.boardHeight) / 2)

    const buttonGap = Math.max(10, Math.floor(width * 0.025))
    const buttonY = height - this.footerHeight + 8
    const buttonH = Math.max(42, this.footerHeight - 18)
    const buttonW = (width - padding * 2 - buttonGap) / 2
    this.resetButtonRect = { x: padding, y: buttonY, w: buttonW, h: buttonH }
    this.shareButtonRect = {
      x: padding + buttonW + buttonGap,
      y: buttonY,
      w: buttonW,
      h: buttonH
    }
    const modalW = Math.min(width - padding * 2, 420)
    const modalH = Math.min(290, Math.max(244, height * 0.48))
    this.resultModalRect = {
      x: (width - modalW) / 2,
      y: Math.max(20, (height - modalH) / 2),
      w: modalW,
      h: modalH
    }
    this.resultButtonRect = {
      x: this.resultModalRect.x + 24,
      y: this.resultModalRect.y + this.resultModalRect.h - 66,
      w: this.resultModalRect.w - 48,
      h: 46
    }
    this.headerPadding = padding
  }

  bindInput() {
    this.web.onTouchEnd((event) => {
      const touch = event.changedTouches?.[0]
      if (!touch) return
      this.handleTap(touch.clientX, touch.clientY)
    })
  }

  pointInRect(x, y, rect) {
    return Boolean(
      rect && x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h
    )
  }

  pickCell(x, y) {
    const localX = x - this.boardX
    const localY = y - this.boardY
    if (localX < 0 || localY < 0) return null
    const c = Math.floor(localX / this.cell)
    const r = Math.floor(localY / this.cell)
    return this.game.inBounds(r, c) ? { r, c } : null
  }

  handleTap(x, y) {
    const now = Date.now()
    const state = this.game.getState(now)
    if (state.status !== 'playing') {
      if (this.pointInRect(x, y, this.resultButtonRect)) {
        if (state.status === 'won') this.startLevel(state.level + 1)
        else this.startLevel(state.level, this.levelSeed)
      }
      return
    }

    if (this.pointInRect(x, y, this.resetButtonRect)) {
      this.startLevel(state.level, this.levelSeed)
      return
    }
    if (this.pointInRect(x, y, this.shareButtonRect)) {
      this.shareProgress(state)
      return
    }

    const cell = this.pickCell(x, y)
    if (!cell) return
    const result = this.game.rotate(cell.r, cell.c, now)
    if (result.ok) {
      this.pressedCell = cell
      this.pressedAt = performance.now()
    } else if (result.reason === 'fixed') {
      this.showToast('LOCKED NODE')
    }
    this.handleFinishIfNeeded()
  }

  showToast(message) {
    this.toast = { message, expiresAt: Date.now() + 900 }
  }

  async shareProgress(state) {
    const result = await this.web.share({
      title: `Neon Circuit level ${state.level}: ${state.poweredTerminals}/${state.terminalCount} online`,
      query: `game=circuit&level=${state.level}`
    })
    this.showToast(result.ok ? 'LINK READY' : 'SHARE UNAVAILABLE')
  }

  startLevel(level, seed = null) {
    this.level = level
    this.game = this.createGame(level, seed)
    this.game.start(Date.now())
    this.lastStatus = this.game.status
    this.finishHandled = false
    this.pressedCell = null
    this.toast = null
    this.resize()
  }

  handleFinishIfNeeded() {
    if (this.game.status === 'playing' || this.finishHandled) return
    this.finishHandled = true
    this.lastStatus = this.game.status
    if (this.game.status === 'won') {
      try {
        const stored = Number(localStorage.getItem('neon-circuit-best-level') || 0)
        if (this.game.level > stored) {
          localStorage.setItem('neon-circuit-best-level', String(this.game.level))
        }
      } catch {}
    }

    // Paint the result immediately; the ad request proceeds independently.
    setTimeout(() => {
      Promise.resolve(this.adbut?.show()).catch((error) => {
        console.warn('[Neon Circuit] Interstitial unavailable:', error)
      })
    }, 250)
  }

  roundedRect(x, y, w, h, radius, fillStyle, strokeStyle = null) {
    const ctx = this.ctx
    const r = Math.max(0, Math.min(radius, Math.min(w, h) / 2))
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r)
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h)
    ctx.quadraticCurveTo(x, y + h, x, y + h - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
    if (fillStyle) {
      ctx.fillStyle = fillStyle
      ctx.fill()
    }
    if (strokeStyle) {
      ctx.strokeStyle = strokeStyle
      ctx.stroke()
    }
  }

  fitText(text, maxWidth, preferredSize, minimumSize = 11, weight = '') {
    let size = preferredSize
    do {
      this.ctx.font = `${weight}${size}px Inter, system-ui, sans-serif`
      if (this.ctx.measureText(text).width <= maxWidth) return size
      size -= 1
    } while (size > minimumSize)
    return size
  }

  drawBackground(now) {
    const ctx = this.ctx
    const width = this.canvas.width
    const height = this.canvas.height
    const background = ctx.createLinearGradient(0, 0, width, height)
    background.addColorStop(0, '#070b12')
    background.addColorStop(0.52, '#111923')
    background.addColorStop(1, '#081319')
    ctx.fillStyle = background
    ctx.fillRect(0, 0, width, height)

    const grid = Math.max(30, Math.floor(Math.min(width, height) * 0.07))
    ctx.strokeStyle = 'rgba(132, 158, 182, 0.075)'
    ctx.lineWidth = 1
    ctx.beginPath()
    for (let x = (now * 0.006) % grid; x < width; x += grid) {
      ctx.moveTo(x, 0)
      ctx.lineTo(x, height)
    }
    for (let y = 0; y < height; y += grid) {
      ctx.moveTo(0, y)
      ctx.lineTo(width, y)
    }
    ctx.stroke()

    const topLight = ctx.createRadialGradient(width * 0.25, 0, 0, width * 0.25, 0, width * 0.8)
    topLight.addColorStop(0, 'rgba(70, 239, 215, 0.12)')
    topLight.addColorStop(1, 'rgba(70, 239, 215, 0)')
    ctx.fillStyle = topLight
    ctx.fillRect(0, 0, width, height)

    const sideLight = ctx.createRadialGradient(width, height * 0.7, 0, width, height * 0.7, width)
    sideLight.addColorStop(0, 'rgba(244, 98, 121, 0.09)')
    sideLight.addColorStop(1, 'rgba(244, 98, 121, 0)')
    ctx.fillStyle = sideLight
    ctx.fillRect(0, 0, width, height)
  }

  drawHeader(state) {
    const ctx = this.ctx
    const p = this.headerPadding
    const width = this.canvas.width
    const titleSize = Math.max(22, Math.min(34, Math.floor(width * 0.075)))
    ctx.fillStyle = '#f3f7fb'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`
    ctx.fillText('NEON CIRCUIT', p, p + titleSize)
    ctx.fillStyle = '#6fe7d4'
    ctx.font = `600 ${Math.max(10, Math.floor(titleSize * 0.37))}px Inter, system-ui, sans-serif`
    ctx.fillText(`POWER GRID // LEVEL ${state.level}`, p, p + titleSize + 20)

    const statsY = p + titleSize + 31
    const gap = Math.max(6, Math.floor(width * 0.015))
    const statW = (width - p * 2 - gap * 2) / 3
    const statH = Math.max(38, this.headerHeight - statsY - 8)
    const seconds = state.remainingSeconds
    const statData = [
      { label: 'NODES', value: `${state.poweredTerminals}/${state.terminalCount}`, accent: '#56e8d1' },
      { label: 'TIME', value: `${seconds}s`, accent: seconds <= 15 ? '#ff6f7f' : '#f0c75e' },
      { label: 'TURNS', value: String(state.moves), accent: '#bfc9d7' }
    ]
    for (let i = 0; i < statData.length; i++) {
      const stat = statData[i]
      const x = p + i * (statW + gap)
      this.roundedRect(x, statsY, statW, statH, 6, 'rgba(20, 29, 40, 0.88)', 'rgba(151, 174, 197, 0.16)')
      ctx.fillStyle = '#8797aa'
      ctx.font = `600 ${Math.max(9, Math.min(11, statH * 0.25))}px Inter, system-ui, sans-serif`
      ctx.fillText(stat.label, x + 10, statsY + 14)
      ctx.fillStyle = stat.accent
      ctx.font = `700 ${Math.max(14, Math.min(19, statH * 0.42))}px Inter, system-ui, sans-serif`
      ctx.fillText(stat.value, x + 10, statsY + statH - 8)
    }
  }

  drawBoardFrame() {
    const ctx = this.ctx
    const margin = Math.max(7, this.cell * 0.1)
    const x = this.boardX - margin
    const y = this.boardY - margin
    const w = this.boardWidth + margin * 2
    const h = this.boardHeight + margin * 2
    ctx.lineWidth = 1.5
    this.roundedRect(x, y, w, h, 8, 'rgba(4, 8, 13, 0.88)', 'rgba(98, 225, 206, 0.28)')

    const corner = Math.max(8, this.cell * 0.17)
    ctx.strokeStyle = '#e4b95a'
    ctx.lineWidth = Math.max(1.5, this.cell * 0.025)
    const corners = [
      [x, y, 1, 1],
      [x + w, y, -1, 1],
      [x, y + h, 1, -1],
      [x + w, y + h, -1, -1]
    ]
    for (const [cx, cy, sx, sy] of corners) {
      ctx.beginPath()
      ctx.moveTo(cx + sx * 2, cy + sy * corner)
      ctx.lineTo(cx + sx * 2, cy + sy * 2)
      ctx.lineTo(cx + sx * corner, cy + sy * 2)
      ctx.stroke()
    }
  }

  directionEnd(direction, length) {
    return {
      x: direction.dc * length,
      y: direction.dr * length
    }
  }

  drawCircuit(mask, powered, x, y, now) {
    const ctx = this.ctx
    const cx = x + this.cell / 2
    const cy = y + this.cell / 2
    const length = this.cell * 0.46
    const lineWidth = Math.max(4, this.cell * 0.11)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    ctx.strokeStyle = '#1d2732'
    ctx.lineWidth = lineWidth + Math.max(3, this.cell * 0.055)
    ctx.beginPath()
    for (const direction of DIRECTIONS) {
      if ((mask & direction.bit) === 0) continue
      const end = this.directionEnd(direction, length)
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + end.x, cy + end.y)
    }
    ctx.stroke()

    ctx.shadowColor = powered ? '#45f0d2' : 'transparent'
    ctx.shadowBlur = powered ? Math.max(6, this.cell * 0.16) : 0
    ctx.strokeStyle = powered ? '#54e8cf' : '#647184'
    ctx.lineWidth = lineWidth
    ctx.beginPath()
    for (const direction of DIRECTIONS) {
      if ((mask & direction.bit) === 0) continue
      const end = this.directionEnd(direction, length)
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + end.x, cy + end.y)
    }
    ctx.stroke()
    ctx.shadowBlur = 0

    ctx.fillStyle = powered ? '#d9fff8' : '#9aa7b7'
    ctx.beginPath()
    ctx.arc(cx, cy, Math.max(3, this.cell * 0.075), 0, Math.PI * 2)
    ctx.fill()

    if (powered) {
      const phase = ((now * 0.0012) % 1) * length
      ctx.fillStyle = '#ffffff'
      for (const direction of DIRECTIONS) {
        if ((mask & direction.bit) === 0) continue
        const ratio = phase / length
        ctx.beginPath()
        ctx.arc(
          cx + direction.dc * length * ratio,
          cy + direction.dr * length * ratio,
          Math.max(1.5, this.cell * 0.035),
          0,
          Math.PI * 2
        )
        ctx.fill()
      }
    }
    ctx.lineCap = 'butt'
  }

  drawTerminal(cell, x, y, now) {
    const ctx = this.ctx
    const cx = x + this.cell / 2
    const cy = y + this.cell / 2
    const pulse = 0.86 + Math.sin(now * 0.008) * 0.1
    const radius = this.cell * 0.18
    ctx.shadowColor = cell.powered ? '#f3c75d' : 'transparent'
    ctx.shadowBlur = cell.powered ? this.cell * 0.25 : 0
    ctx.fillStyle = cell.powered ? '#f4c85e' : '#2c333d'
    ctx.beginPath()
    ctx.arc(cx, cy, radius * pulse, 0, Math.PI * 2)
    ctx.fill()
    ctx.shadowBlur = 0
    ctx.strokeStyle = cell.powered ? '#fff0b5' : '#d49d4a'
    ctx.lineWidth = Math.max(2, this.cell * 0.035)
    ctx.beginPath()
    ctx.arc(cx, cy, radius * 1.35, 0, Math.PI * 2)
    ctx.stroke()
    ctx.fillStyle = cell.powered ? '#fff8dc' : '#8b6840'
    ctx.beginPath()
    ctx.arc(cx, cy, Math.max(2, radius * 0.3), 0, Math.PI * 2)
    ctx.fill()
  }

  drawSource(cell, x, y, now) {
    const ctx = this.ctx
    const cx = x + this.cell / 2
    const cy = y + this.cell / 2
    const radius = this.cell * (0.19 + Math.sin(now * 0.006) * 0.012)
    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(Math.PI / 4)
    ctx.shadowColor = '#ff6683'
    ctx.shadowBlur = this.cell * 0.25
    ctx.fillStyle = cell.powered ? '#f45d79' : '#733446'
    ctx.fillRect(-radius, -radius, radius * 2, radius * 2)
    ctx.shadowBlur = 0
    ctx.strokeStyle = '#ffd2da'
    ctx.lineWidth = Math.max(1.5, this.cell * 0.03)
    ctx.strokeRect(-radius, -radius, radius * 2, radius * 2)
    ctx.restore()
    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.arc(cx, cy, Math.max(2, this.cell * 0.045), 0, Math.PI * 2)
    ctx.fill()
  }

  drawBoard(state, now) {
    this.drawBoardFrame()
    const ctx = this.ctx
    const gap = Math.max(2, this.cell * 0.045)
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        const cell = state.board[r][c]
        const x = this.boardX + c * this.cell
        const y = this.boardY + r * this.cell
        const pressed =
          this.pressedCell?.r === r &&
          this.pressedCell?.c === c &&
          now - this.pressedAt < 180
        const tileFill = cell.powered
          ? 'rgba(24, 63, 62, 0.94)'
          : pressed
            ? 'rgba(45, 54, 66, 0.98)'
            : 'rgba(18, 25, 35, 0.96)'
        ctx.lineWidth = 1
        this.roundedRect(
          x + gap,
          y + gap,
          this.cell - gap * 2,
          this.cell - gap * 2,
          Math.max(4, this.cell * 0.09),
          tileFill,
          cell.powered ? 'rgba(80, 235, 208, 0.2)' : 'rgba(142, 158, 176, 0.12)'
        )
        this.drawCircuit(cell.mask, cell.powered, x, y, now)
        if (cell.terminal) this.drawTerminal(cell, x, y, now)
        if (cell.source) this.drawSource(cell, x, y, now)
      }
    }
  }

  drawButton(rect, label, primary = false) {
    const ctx = this.ctx
    const fill = primary ? '#d3a943' : 'rgba(39, 51, 65, 0.96)'
    const border = primary ? '#f9dc87' : 'rgba(147, 168, 190, 0.28)'
    ctx.lineWidth = 1.5
    this.roundedRect(rect.x, rect.y, rect.w, rect.h, 6, fill, border)
    ctx.fillStyle = primary ? '#15130f' : '#e8eef5'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const size = this.fitText(label, rect.w - 20, Math.min(15, rect.h * 0.32), 11, '700 ')
    ctx.font = `700 ${size}px Inter, system-ui, sans-serif`
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  drawFooter() {
    this.drawButton(this.resetButtonRect, 'RESET GRID')
    this.drawButton(this.shareButtonRect, 'SHARE')
  }

  drawStar(cx, cy, radius, filled) {
    const ctx = this.ctx
    ctx.beginPath()
    for (let i = 0; i < 10; i++) {
      const angle = -Math.PI / 2 + (Math.PI * i) / 5
      const r = i % 2 === 0 ? radius : radius * 0.45
      const x = cx + Math.cos(angle) * r
      const y = cy + Math.sin(angle) * r
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
    ctx.fillStyle = filled ? '#f0c45b' : '#313b47'
    ctx.fill()
    ctx.strokeStyle = filled ? '#ffe69b' : '#667486'
    ctx.lineWidth = 1
    ctx.stroke()
  }

  drawResult(state) {
    const ctx = this.ctx
    ctx.fillStyle = 'rgba(3, 6, 10, 0.78)'
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    const modal = this.resultModalRect
    ctx.lineWidth = 1.5
    this.roundedRect(
      modal.x,
      modal.y,
      modal.w,
      modal.h,
      8,
      'rgba(15, 22, 31, 0.98)',
      state.status === 'won' ? 'rgba(83, 237, 210, 0.58)' : 'rgba(255, 101, 124, 0.5)'
    )
    ctx.textAlign = 'center'
    ctx.fillStyle = state.status === 'won' ? '#79f3dd' : '#ff8496'
    const title = state.status === 'won' ? 'GRID ONLINE' : 'POWER LOST'
    const titleSize = this.fitText(title, modal.w - 40, 30, 20, '800 ')
    ctx.font = `800 ${titleSize}px Inter, system-ui, sans-serif`
    ctx.fillText(title, modal.x + modal.w / 2, modal.y + 46)

    if (state.status === 'won') {
      const starY = modal.y + 82
      for (let i = 0; i < 3; i++) {
        this.drawStar(modal.x + modal.w / 2 + (i - 1) * 38, starY, 14, i < state.stars)
      }
      ctx.fillStyle = '#f2f5f8'
      ctx.font = '700 24px Inter, system-ui, sans-serif'
      ctx.fillText(String(state.score), modal.x + modal.w / 2, modal.y + 126)
      ctx.fillStyle = '#8998aa'
      ctx.font = '600 11px Inter, system-ui, sans-serif'
      ctx.fillText('TOTAL OUTPUT', modal.x + modal.w / 2, modal.y + 144)
    } else {
      ctx.fillStyle = '#dfe6ee'
      ctx.font = '700 20px Inter, system-ui, sans-serif'
      ctx.fillText(
        `${state.poweredTerminals}/${state.terminalCount} NODES`,
        modal.x + modal.w / 2,
        modal.y + 95
      )
    }

    ctx.fillStyle = '#aeb9c7'
    ctx.font = '600 13px Inter, system-ui, sans-serif'
    ctx.fillText(
      `${state.moves} TURNS  /  ${state.remainingSeconds}s LEFT`,
      modal.x + modal.w / 2,
      modal.y + modal.h - 88
    )
    this.drawButton(
      this.resultButtonRect,
      state.status === 'won' ? 'NEXT GRID' : 'TRY AGAIN',
      true
    )
    ctx.textAlign = 'left'
  }

  drawToast() {
    if (!this.toast || Date.now() >= this.toast.expiresAt) {
      this.toast = null
      return
    }
    const ctx = this.ctx
    const message = this.toast.message
    ctx.font = '700 12px Inter, system-ui, sans-serif'
    const width = Math.min(this.canvas.width - 32, ctx.measureText(message).width + 34)
    const x = (this.canvas.width - width) / 2
    const y = this.boardY + this.boardHeight - 52
    this.roundedRect(x, y, width, 36, 5, 'rgba(6, 10, 15, 0.92)', 'rgba(105, 230, 211, 0.38)')
    ctx.fillStyle = '#dffaf5'
    ctx.textAlign = 'center'
    ctx.fillText(message, this.canvas.width / 2, y + 23)
    ctx.textAlign = 'left'
  }

  render(animationNow) {
    const now = Date.now()
    this.game.tick(now)
    this.handleFinishIfNeeded()
    const state = this.game.getState(now)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.drawBackground(animationNow)
    this.drawHeader(state)
    this.drawBoard(state, animationNow)
    this.drawFooter()
    this.drawToast()
    if (state.status !== 'playing') this.drawResult(state)
  }

  loop() {
    const tick = (now) => {
      this.render(now || performance.now())
      this.requestFrame(tick)
    }
    this.requestFrame(tick)
  }
}

module.exports = { CanvasCircuitApp }
